import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  FamilyRestroom as FamilyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Pets as PetsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AccountTree as PedigreeIcon,
} from '@mui/icons-material';
import { Litter } from '../../types';
import littersService from '@services/littersService';
import { formatDateOnly } from '@utils/dateFormatter';

interface AnimalReproTabProps {
  animalId: string;
  animalGender?: 'male' | 'female';
  onOpenLitterDialog: () => void;
  onOpenPedigreeModal: () => void;
}

const AnimalReproTab: React.FC<AnimalReproTabProps> = ({
  animalId,
  animalGender,
  onOpenLitterDialog,
  onOpenPedigreeModal,
}) => {
  const [litters, setLitters] = useState<Litter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLitter, setSelectedLitter] = useState<Litter | null>(null);

  useEffect(() => {
    loadLitters();
  }, [animalId]);

  const loadLitters = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await littersService.getAnimalLitters(animalId, 'both');
      setLitters(data);
    } catch (err) {
      console.error('Error loading litters:', err);
      setError('Eroare la incarcarea fatarilor');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, litter: Litter) => {
    setAnchorEl(event.currentTarget);
    setSelectedLitter(litter);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLitter(null);
  };

  const handleEditLitter = () => {
    handleMenuClose();
    // TODO: Open edit dialog
    console.log('Edit litter:', selectedLitter);
  };

  const handleManagePups = () => {
    handleMenuClose();
    // TODO: Open pups management dialog
    console.log('Manage pups:', selectedLitter);
  };

  const handleDeleteLitter = async () => {
    if (!selectedLitter) return;

    if (window.confirm('Sigur doriti sa stergeti aceasta fatare?')) {
    try {
      await littersService.deleteLitter(selectedLitter.id);
      await loadLitters();
        handleMenuClose();
      } catch (err) {
      console.error('Error deleting litter:', err);
      alert('Eroare la stergerea fatarii');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header cu butoane */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Reproducere
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<PedigreeIcon />}
            onClick={onOpenPedigreeModal}
          >
            Pedigree
          </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
            onClick={onOpenLitterDialog}
            >
            Adauga Fatare
            </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Empty state */}
      {litters.length === 0 && (
        <Card>
          <CardContent>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight={200}
              textAlign="center"
            >
              <FamilyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Nu exista fatari inregistrate
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Adauga prima fatare pentru a lega pui prin microchip
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onOpenLitterDialog}
                sx={{ mt: 2 }}
              >
                Adauga Fatare
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Lista fatari */}
      {litters.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {litters.map((litter) => (
            <Box key={litter.id} sx={{ width: { xs: '100%', md: 'calc(50% - 8px)' } }}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <FamilyIcon color="primary" />
                        <Typography variant="h6">
                          Fatare {formatDateOnly(litter.whelping_date)}
                        </Typography>
                        <Chip
                          icon={litter.is_complete ? <CheckCircleIcon /> : <WarningIcon />}
                          label={litter.is_complete ? 'Completa' : 'Incompleta'}
                          color={litter.is_complete ? 'success' : 'warning'}
                          size="small"
                        />
                      </Stack>

                      <Divider sx={{ my: 1 }} />

                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Mama:</strong>{' '}
                          {litter.mother?.name || 'Necunoscuta'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Tata:</strong>{' '}
                          {litter.father?.name || 'Necunoscut'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Pui declarati:</strong> {litter.pups_declared}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Pui legati:</strong>{' '}
                          {litter.pups_linked || 0} / {litter.pups_declared}
                        </Typography>
                      </Stack>

                      {litter.notes && (
                        <Box mt={2}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Notite:</strong> {litter.notes}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, litter)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      {/* Menu actiuni */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditLitter}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editeaza Fatare
        </MenuItem>
        <MenuItem onClick={handleManagePups}>
          <PetsIcon fontSize="small" sx={{ mr: 1 }} />
          Gestioneaza Pui
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteLitter} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Sterge Fatare
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AnimalReproTab;
